import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import MovieCard from "@/components/base/MovieCard";
import MovieCardSkeleton from "@/components/base/MovieCardSkeleton";
import { movies, getHotMovies, getHotSeries } from "@/mocks/movies";
import { usePageMeta } from "@/hooks/usePageMeta";
import HeroCarousel from "./components/HeroCarousel";
import CategoryEntry from "./components/CategoryEntry";

const featured = movies.filter((movie) => movie.featured);
const hotMovies = getHotMovies(10);
const hotSeries = getHotSeries(5);

const stats = [
  { value: "19,200+", label: "影视资源条目" },
  { value: "36", label: "细分内容类型" },
  { value: "4K", label: "高清封面展示" },
  { value: "7×24", label: "内容持续更新" },
];

export default function Home() {
  const [loading, setLoading] = useState(true);

  usePageMeta({
    title: "嫩草影院 - 高清电影电视剧在线播放平台",
    description:
      "嫩草影院提供丰富的电影和电视剧资源，涵盖各类热门影视内容，支持多终端在线观看，满足用户观影需求。",
    keywords:
      "嫩草影院,电影在线观看,在线电视剧,高清影视,最新电影,热门电视剧,影视平台",
  });

  useEffect(() => {
    const timer = window.setTimeout(() => setLoading(false), 500);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar overlay />

      <main>
        <HeroCarousel movies={featured} />

        <section className="w-full px-4 md:px-8 py-10 md:py-14">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6 md:mb-8">
            <div>
              <p className="text-[11px] tracking-[0.3em] text-primary-500">CATEGORIES</p>
              <h2 className="mt-2 text-xl md:text-2xl font-bold text-foreground-950">
                按分类快速找到想看的
              </h2>
              <p className="mt-2 text-sm text-foreground-600">
                两大片库入口，进入后可按类型、地区与年代继续筛选
              </p>
            </div>
            <Link
              to="/category"
              className="inline-flex items-center gap-1.5 text-sm text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap"
            >
              查看全部影片
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <CategoryEntry />
        </section>

        <section className="w-full bg-background-100/60 border-y border-background-300/70">
          <div className="w-full px-4 md:px-8 py-10 md:py-14">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6 md:mb-8">
              <div>
                <p className="text-[11px] tracking-[0.3em] text-accent-500">TRENDING NOW</p>
                <h2 className="mt-2 text-xl md:text-2xl font-bold text-foreground-950">
                  热门电影推荐
                </h2>
                <p className="mt-2 text-sm text-foreground-600">
                  近期播放热度与评分双高的院线作品
                </p>
              </div>
              <Link
                to="/category?type=movie&sort=hot"
                className="inline-flex items-center gap-1.5 text-sm text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap"
              >
                更多热门电影
                <i className="ri-arrow-right-line" />
              </Link>
            </div>

            {loading ? (
              <MovieCardSkeleton count={10} />
            ) : hotMovies.length === 0 ? (
              <div className="py-16 text-center rounded-lg bg-background-100 border border-background-300/70">
                <i className="ri-film-line text-3xl text-foreground-500" />
                <p className="mt-3 text-sm text-foreground-600">暂无热门推荐，敬请期待</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-5">
                {hotMovies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-10 md:py-14">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6 md:mb-8">
            <div>
              <p className="text-[11px] tracking-[0.3em] text-primary-500">BINGE WORTHY</p>
              <h2 className="mt-2 text-xl md:text-2xl font-bold text-foreground-950">
                高分剧集精选
              </h2>
              <p className="mt-2 text-sm text-foreground-600">
                一集入坑，适合周末连续观看的口碑剧集
              </p>
            </div>
            <Link
              to="/category?type=tv&sort=rating"
              className="inline-flex items-center gap-1.5 text-sm text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap"
            >
              更多剧集
              <i className="ri-arrow-right-line" />
            </Link>
          </div>

          {loading ? (
            <MovieCardSkeleton count={5} />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-5">
              {hotSeries.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          )}
        </section>

        <section className="w-full px-4 md:px-8 pb-4">
          <div className="rounded-lg bg-background-300 border border-background-400/70 px-5 md:px-10 py-8 md:py-10">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center lg:text-left">
                  <p className="font-display text-3xl md:text-4xl font-semibold text-accent-500">
                    {stat.value}
                  </p>
                  <p className="mt-1.5 text-xs md:text-sm text-foreground-600">{stat.label}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 pt-6 border-t border-background-400/70 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <p className="text-sm text-foreground-700 leading-6 max-w-xl">
                清晰的分类结构、完整的影片资料、稳定的浏览体验——嫩草影院希望你少花时间找片，多花时间看片。
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <Link
                  to="/category"
                  className="h-10 px-5 inline-flex items-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-colors whitespace-nowrap"
                >
                  浏览全部影片
                </Link>
                <Link
                  to="/about"
                  className="h-10 px-5 inline-flex items-center gap-2 rounded-md bg-background-100 hover:bg-background-200 border border-background-400 text-foreground-900 text-sm transition-colors whitespace-nowrap"
                >
                  了解我们
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}